import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Typography, Card, CardMedia, CardContent,
  Button, Grid, CircularProgress, Box, Dialog, DialogTitle,
  DialogContent, DialogActions, List, ListItem, ListItemText,
  CssBaseline
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { useSearchParams, useNavigate } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import TopBar from "./components/TopBar";
import { API_BASE } from "./config";
import { useCustomerAuth } from "./auth/CustomerAuthContext";
import Footer from "./components/Footer";


export default function Machine() {
  const [searchParams] = useSearchParams();
  const uid = searchParams.get("uid");
  const navigate = useNavigate();
  const { customerToken } = useCustomerAuth();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [location, setLocation] = useState("");
  const [serial, setSerial] = useState("");
  const [mode, setMode] = useState("light");

  const toggleTheme = () => setMode((prev) => (prev === "light" ? "dark" : "light"));
  const theme = useMemo(() => createTheme({ palette: { mode } }), [mode]);

  useEffect(() => {
    if (!uid) return;

    const fetchProducts = async () => {
      setLoading(true);
      setError(null);

      try {
        const headers = customerToken
          ? {}
          : {};

        const response = await fetch(`${API_BASE}/zts/app/v1.0/list/product/${uid}/`, {
          headers
        });

        if (response.status === 401) {
          navigate(`/customer-login?next=/private-machine?uid=${uid}`);
          return;
        }

        if (!response.ok) throw new Error("Failed to fetch products");

        const data = await response.json();
        setProducts(data.product || []);
        setLocation(data.location || "");
        setSerial(data.serial || "");
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Failed to load products.");
        setProducts([]);
        setLocation("");
        setSerial("");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [uid, navigate, customerToken]);

  const handleBuyNow = async (product) => {
    const sessionid = uuidv4();
    setActionLoading(true);

    try {
      const response = await fetch(`${API_BASE}/zts/app/v1.0/sales/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          itemid: product.id,
          sessionid,
          category: "sales",
          event: "create"
        })
      });

      if (!response.ok) throw new Error("Failed to save sales record");

      const resData = await response.json();
      setPaymentData({
        order_id: resData.data.id,
        sessionid,
        amount: resData.data.amount,
        name: product.item_name
      });

      setSelectedProduct(product);
      setCheckoutOpen(true);
    } catch (error) {
      console.error("Error saving sale record:", error);
      alert("Failed to process sale. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckoutClose = async () => {
    if (paymentData && selectedProduct) {
      setActionLoading(true);
      try {
        await fetch(`${API_BASE}/zts/app/v1.0/sales/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            sessionid: paymentData.sessionid,
            itemid: selectedProduct.id,
            category: "sales",
            event: "cancel"
          })
        });
      } catch (error) {
        console.error("Error canceling sale:", error);
      } finally {
        setActionLoading(false);
      }
    }

    setCheckoutOpen(false);
    setSelectedProduct(null);
    setPaymentData(null);
  };

  const handleProceedToPayment = () => {
    if (!paymentData) return;
    setActionLoading(true);

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;

    script.onload = () => {
      const options = {
        key: "rzp_live_RO1G8unS3F7df0",
        amount: paymentData.amount.toString(),
        currency: "MYR",
        name: paymentData.name,
        description: paymentData.sessionid,
        image: "https://vendinapi.hphttech.com/static/logo.png",
        order_id: paymentData.order_id,
        callback_url: `https://vending.hphttech.com/payment-success?session_id=${paymentData.sessionid}&from=${uid}`,
        redirect: true,
        prefill: {
          name: "guest",
          email: "guest@hphttech.com",
          contact: "+60123456789"
        },
        notes: { sessionid: paymentData.sessionid },
        theme: { color: theme.palette.primary.main }
      };

      const razor = new window.Razorpay(options);
      razor.open();
      setActionLoading(false);
    };

    document.body.appendChild(script);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <TopBar
        serial={serial}
        location={location}
        mode={mode}
        toggleTheme={toggleTheme}
      />

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 3 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Loading...</Typography>
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Grid container spacing={3}>
            {products.map((product) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "0.2s",
                    "&:hover": { boxShadow: 5 }
                  }}
                >
                  <CardMedia
                    component="img"
                    height="160"
                    image={product.item_image_url}
                    alt={product.item_name}
                    sx={{ objectFit: "contain", p: 2 }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom noWrap>{product.item_name}</Typography>
                    <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                      RM{parseFloat(product.price).toFixed(2)}
                    </Typography>
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0 }}>
                    {parseInt(product.balance) === 0 ? (
                      <Typography variant="button" color="error">OUT OF STOCK</Typography>
                    ) : (
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => handleBuyNow(product)}
                        disabled={actionLoading}
                      >
                        {actionLoading ? <CircularProgress size={20} color="inherit" /> : "Buy Now"}
                      </Button>
                    )}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Dialog open={checkoutOpen} onClose={handleCheckoutClose} maxWidth="xs" fullWidth>
          <DialogTitle>Checkout</DialogTitle>
          <DialogContent>
            {selectedProduct && (
              <List>
                <ListItem>
                  <ListItemText
                    primary={selectedProduct.item_name}
                    secondary={`RM${parseFloat(selectedProduct.price).toFixed(2)}`}
                  />
                </ListItem>
              </List>
            )}
            <Typography variant="h6" sx={{ mt: 2 }}>
              Total: RM{selectedProduct ? parseFloat(selectedProduct.price).toFixed(2) : "0.00"}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCheckoutClose} disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} color="inherit" /> : "Cancel"}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleProceedToPayment}
              disabled={actionLoading}
            >
              {actionLoading ? <CircularProgress size={20} color="inherit" /> : "Proceed to Payment"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* <Box sx={{ mt: 6, py: 3, textAlign: "center", color: "text.secondary" }}>
          &copy; {new Date().getFullYear()} HPHT Vending
        </Box> */}

        <Footer />
      </Container>
    </ThemeProvider>
  );
}


